import React, { useMemo } from 'react';

// Components
import Icon from '../../../../../../../components/icons/Icon';
import SheetMenu from '../../../../../../../mobile/structural/sheet/menu/SheetMenu';

// Utils
import { isOwner, isFullAccess, isBlocked } from '../../../../../../../../common/permissions/permissionUtil';
import { userIsRegistered } from '../../../../../../../../common/users/userHelper';

// Types
import { MNUser } from '../../../../../../../../common/users/userModelTypes';

type MobileBoardEditorPermissionSheetMenuProps = {
    name: string;
    userPermissions: number;
    permission: number;
    user: MNUser;
    setUserPermissionsFull: () => void;
    onShowConfirmation: (message: string, onConfirmAction: () => void) => void;
    removeSharedUser: () => void;
    dispatchTransferBoardOwnership: () => void;
};

const MobileBoardEditorPermissionSheetMenu = ({
    name,
    userPermissions,
    permission,
    user,
    setUserPermissionsFull,
    onShowConfirmation,
    removeSharedUser,
    dispatchTransferBoardOwnership,
}: MobileBoardEditorPermissionSheetMenuProps) => {
    // Build menu items based on current permissions
    const menuItems = useMemo(() => {
        const items = [];

        // Option to make another user the owner (only if current user is owner and target has full access)
        if (isOwner(userPermissions) && isFullAccess(permission) && userIsRegistered(user)) {
            items.push({
                label: `Make ${name} the owner`,
                icon: <Icon name="context-menu-transfer" />,
                onClick: () =>
                    onShowConfirmation(
                        `Are you sure you want to make ${name} the owner?`,
                        dispatchTransferBoardOwnership,
                    ),
            });
        }

        // Option to unblock or give full access
        if (!isFullAccess(permission)) {
            items.push({
                label: isBlocked(permission) ? `Unblock ${name}` : `Give ${name} full edit access`,
                icon: <Icon name={isBlocked(permission) ? 'unlock' : 'edit'} />,
                onClick: setUserPermissionsFull,
            });
        }

        // Option to remove user from board
        if (!isBlocked(permission)) {
            items.push({
                label: `Remove ${name}`,
                icon: <Icon name="context-menu-remove" />,
                onClick: () => onShowConfirmation(`Are you sure you want to remove ${name}?`, removeSharedUser),
            });
        }

        return items;
    }, [
        name,
        userPermissions,
        permission,
        user,
        onShowConfirmation,
        dispatchTransferBoardOwnership,
        setUserPermissionsFull,
        removeSharedUser,
    ]);

    return <SheetMenu menuItems={menuItems} />;
};

export default MobileBoardEditorPermissionSheetMenu;
